import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

type Service = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

type ServiceGroup = {
  eyebrow: string;
  title: string;
  description: string;
  services: Service[];
};

const groups: ServiceGroup[] = [
  {
    eyebrow: 'Build from scratch',
    title: 'Custom software, designed around your workflow.',
    description:
      'Production-grade platforms, products, and tools — engineered from first principles, owned by you.',
    services: [
      {
        title: 'Custom Web Platforms',
        description:
          'Production web applications built around your specific workflows, not bent into a template.',
        icon: <path d="M4 6h16v12H4z M4 10h16 M9 6v12" />,
      },
      {
        title: 'SaaS Products',
        description:
          'From validated idea to multi-tenant SaaS with billing, auth, and admin tooling that scales with you.',
        icon: <path d="M3 12a9 9 0 1118 0 9 9 0 01-18 0z M3 12h18 M12 3a14 14 0 010 18 M12 3a14 14 0 000 18" />,
      },
      {
        title: 'Internal Tools & ERPs',
        description:
          'Operations consoles, dashboards, and back-office systems that replace the spreadsheet sprawl.',
        icon: <path d="M4 4h6v6H4z M14 4h6v6h-6z M4 14h6v6H4z M14 14h6v6h-6z" />,
      },
      {
        title: 'AI-Powered Features',
        description:
          'Practical AI integrated into real product workflows — assistants, search, automation, summarization.',
        icon: <path d="M12 2v4 M12 18v4 M2 12h4 M18 12h4 M5 5l3 3 M16 16l3 3 M5 19l3-3 M16 8l3-3 M9 12a3 3 0 116 0 3 3 0 01-6 0z" />,
      },
    ],
  },
  {
    eyebrow: 'Web & e-commerce',
    title: 'Websites and online stores that actually sell.',
    description:
      'Marketing sites, landing pages, and full e-commerce — built fast, ranked well, designed to convert.',
    services: [
      {
        title: 'Marketing Websites',
        description:
          'Modern, fast, accessible marketing sites with proper SEO foundations baked in from day one.',
        icon: <path d="M3 5h18v14H3z M3 9h18 M7 5v14" />,
      },
      {
        title: 'Online Stores / E-commerce',
        description:
          'Full e-commerce platforms — catalog, cart, checkout, payments, inventory, and admin — or a headless storefront on Shopify if that fits better.',
        icon: <path d="M6 6h15l-1.5 9h-12L5 3H2 M9 21a1 1 0 100-2 1 1 0 000 2z M18 21a1 1 0 100-2 1 1 0 000 2z" />,
      },
      {
        title: 'Landing Pages & Micro-sites',
        description:
          'Conversion-focused single pages for campaigns, product launches, or specific funnels — built for speed.',
        icon: <path d="M3 4h18v6H3z M3 14h18v6H3z M7 4v6 M7 14v6" />,
      },
      {
        title: 'Headless & Composable Sites',
        description:
          'Decoupled architecture for teams that need editorial freedom plus performance — Next.js + your CMS of choice.',
        icon: <path d="M4 4h7v7H4z M13 4h7v7h-7z M4 13h7v7H4z M13 13h7v7h-7z M11 7.5h2 M11 11h2 M7.5 11v2 M11 16.5h2" />,
      },
    ],
  },
  {
    eyebrow: 'Design & SEO',
    title: 'Make it look right and get it found.',
    description:
      'Visual design and search visibility — treated as engineering disciplines, not afterthoughts.',
    services: [
      {
        title: 'Product Design (UX / UI)',
        description:
          'Interface design that reflects how your users actually think and work, not what looks good on Dribbble.',
        icon: <path d="M3 4h18v14H3z M3 8h18 M7 12h4 M7 15h6" />,
      },
      {
        title: 'Brand-aligned Visual Design',
        description:
          'Marketing site design, brand kits, and product visual systems that stay coherent across surfaces.',
        icon: <path d="M12 2l3 7h7l-5.5 4 2 7-6.5-4.5L5.5 20l2-7L2 9h7z" />,
      },
      {
        title: 'SEO Foundations',
        description:
          'Technical SEO from day one — structured data, sitemaps, performance, indexable architecture, on-page strategy.',
        icon: <path d="M11 19a8 8 0 100-16 8 8 0 000 16z M21 21l-4.35-4.35" />,
      },
      {
        title: 'Performance Optimization',
        description:
          'Core Web Vitals work on sites that already exist — speed, bundle size, render strategy, image pipelines.',
        icon: <path d="M3 12h4l3-9 4 18 3-9h4" />,
      },
    ],
  },
  {
    eyebrow: 'Working with what you already have',
    title: 'Extend, fix, and modernize existing platforms.',
    description:
      'You already paid for a platform that works almost right. We make it work the rest of the way — without a rewrite.',
    services: [
      {
        title: 'Work on Existing Platforms',
        description:
          'Add features, fix long-standing bugs, modernize legacy areas of platforms you already run in production. We slot in without disrupting what works.',
        icon: <path d="M14 3l7 7-11 11H3v-7L14 3z M14 3l4 4 M3 21h7" />,
      },
      {
        title: 'API & System Integrations',
        description:
          'Connect your platform to ERPs, CRMs, payment gateways, accounting, Shopify, Booking, the national HIF, and other internal services so data flows reliably end to end.',
        icon: <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1 M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />,
      },
      {
        title: 'Third-party Platform Customization',
        description:
          'Customizations and integrations on top of Shopify, WordPress, Laravel-based apps, and similar — done properly so future upgrades don\'t break them.',
        icon: <path d="M12 2l9 4-9 4-9-4 9-4z M3 10l9 4 9-4 M3 14l9 4 9-4" />,
      },
      {
        title: 'Migrations & Rewrites',
        description:
          'Move off legacy systems with a clear path: incremental migration, data preservation, no big-bang cutover unless that\'s genuinely the right call.',
        icon: <path d="M4 4v6h6 M4 10a8 8 0 0114-5 M20 20v-6h-6 M20 14a8 8 0 01-14 5" />,
      },
    ],
  },
  {
    eyebrow: 'DevOps & infrastructure',
    title: 'Reliable infrastructure, predictable releases.',
    description:
      'Servers, pipelines, and observability set up so the product stays online and the team can ship without fear.',
    services: [
      {
        title: 'Cloud Setup & Deployment',
        description:
          'AWS, Hetzner, Vercel, DigitalOcean — picked by fit, not fashion. Provisioned, secured, and documented.',
        icon: <path d="M7 18h13a4 4 0 000-8 6 6 0 00-11.7-1.7A4.5 4.5 0 007 18z" />,
      },
      {
        title: 'CI/CD Pipelines',
        description:
          'Automated builds, tests, and deploys so shipping is a non-event. GitHub Actions, GitLab CI, or whatever you already use.',
        icon: <path d="M12 2v4 M12 18v4 M4.93 4.93l2.83 2.83 M16.24 16.24l2.83 2.83 M2 12h4 M18 12h4 M4.93 19.07l2.83-2.83 M16.24 7.76l2.83-2.83" />,
      },
      {
        title: 'Monitoring & Observability',
        description:
          'Logs, metrics, alerts, error tracking, uptime — set up so you find out about problems before users do, not after.',
        icon: <path d="M3 12h4l3-9 4 18 3-9h4" />,
      },
      {
        title: 'Performance & Cost Optimization',
        description:
          'Audit existing infrastructure, find the wasteful bits, and tune. Often pays for itself in one cloud bill.',
        icon: <path d="M12 1v22 M5 8c1 0 2-1 4-1s5 2 6 2 3-1 4-1 M5 16c1 0 2-1 4-1s5 2 6 2 3-1 4-1" />,
      },
    ],
  },
];

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  provider: { '@id': 'https://nexsollabs.com/#organization' },
  serviceType: [
    'Custom Software Development',
    'SaaS Development',
    'Web Development',
    'E-commerce Development',
    'UX/UI Design',
    'SEO',
    'DevOps',
    'API Integrations',
    'Platform Customization',
  ],
  areaServed: [
    { '@type': 'Country', name: 'North Macedonia' },
    { '@type': 'Place', name: 'Balkans' },
    { '@type': 'Place', name: 'Europe' },
  ],
};

const servicesBreadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexsollabs.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://nexsollabs.com/services' },
  ],
};

export default function Services() {
  return (
    <>
      <SEO
        title="Services — Custom Software, Web & E-commerce, Design, SEO, DevOps | Nexsol Labs"
        description="Software services from Skopje: custom software, websites and online stores, e-commerce, SaaS, design, SEO, DevOps, API integrations, and engineering on existing platforms. Built end to end by Nexsol Labs."
        path="/services"
        keywords="software services Skopje, web development Macedonia, e-commerce Skopje, SEO Skopje, custom software solutions, изработка на веб страни Скопје, изработка на e-commerce Македонија, ERP development, API integration Macedonia"
        jsonLd={[servicesJsonLd, servicesBreadcrumbJsonLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-page relative py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="eyebrow">Our services</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              More than custom software.{' '}
              <span className="gradient-text">Everything around it, too.</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
              We build custom software end to end — but we also build the websites, online
              stores, integrations, and infrastructure around it. If you already have a
              platform that almost works, we'll make it work the rest of the way. If you
              need design or SEO on top of that, we cover those too.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Tell us what you need
              </Link>
              <Link to="/projects" className="btn-ghost">
                See our work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICE GROUPS */}
      {groups.map((group, gi) => (
        <section
          key={group.title}
          id={slugify(group.eyebrow)}
          className="container-page scroll-mt-24 py-16 md:py-20"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <p className="eyebrow">{group.eyebrow}</p>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
              {group.title}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/65 md:text-lg">
              {group.description}
            </p>
          </motion.div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {group.services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card flex flex-col"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-teal-500/20 text-accent-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    {s.icon}
                  </svg>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">
                  {s.description}
                </p>
              </motion.div>
            ))}
          </div>

          {gi < groups.length - 1 && (
            <div className="mt-16 border-b border-white/5" />
          )}
        </section>
      ))}

      {/* HOW WE COMBINE */}
      <section className="container-page py-16 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-12"
        >
          <p className="eyebrow">How we combine these</p>
          <h2 className="mt-4 max-w-3xl font-display text-2xl font-semibold leading-tight tracking-tight text-white md:text-3xl">
            Most projects need three of these at once. We don't make you hire three
            different companies for it.
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <div>
              <p className="font-mono text-xs text-accent-400">01</p>
              <h3 className="mt-2 font-display text-base font-semibold text-white">
                One team, one bill
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                Design, build, deploy, optimize — handled by the same small team that
                actually understands the product end to end.
              </p>
            </div>
            <div>
              <p className="font-mono text-xs text-accent-400">02</p>
              <h3 className="mt-2 font-display text-base font-semibold text-white">
                Honest scope
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                We'll tell you when something is a bad idea, when it's overbuilt, and
                when an off-the-shelf tool would actually be enough.
              </p>
            </div>
            <div>
              <p className="font-mono text-xs text-accent-400">03</p>
              <h3 className="mt-2 font-display text-base font-semibold text-white">
                Boring, durable choices
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                We pick the technology that will still be a good decision in three
                years. Excitement is not a strategy.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="container-page py-20 md:py-28">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-accent-500/15 via-teal-500/10 to-transparent p-10 md:p-16">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative grid items-center gap-8 md:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
                Not sure which of these you need?
              </h2>
              <p className="mt-4 max-w-xl text-base text-white/70">
                Describe what you're trying to do. We'll come back with an honest read
                on whether it's one of these, several of these, or none of these.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/contact" className="btn-primary">
                Start a conversation
              </Link>
              <Link to="/projects" className="btn-ghost">
                See our work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
